<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useEmitAsProps,
} from "radix-vue"
import { X } from "lucide-vue-next"
import { cn } from "@/lib/utils"

const props = withDefaults(
  defineProps<
    DialogContentProps & {
      class?: string
      closable?: boolean
      overlay?: boolean
      xSizeClass?: string
      dialogCloseClass?: string
    }
  >(),
  {
    closable: true,
    overlay: true,
  }
)

const emits = defineEmits<DialogContentEmits>()
const emitsAsProps = useEmitAsProps(emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      v-if="props.overlay"
      class="dialog-overlay-animate data-[state=open]:animate-in', 'data-[state=closed]:animate-out bg-grey-darken-1 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 opacity-20 backdrop-blur-xs"
    />
    <DialogContent
      @interact-outside="(event) => !closable && event.preventDefault()"
      :class="
        cn(
          'dialog-content-animate fixed bottom-0 left-[50%] z-50 grid w-full translate-x-[-50%] gap-4',
          'border-border bg-background overflow-y-auto rounded-t-lg border p-5 shadow-lg duration-200',
          'xs:gap-6 smh:gap-4 smh:p-4 lg:top-[50%] lg:bottom-auto lg:w-full lg:max-w-lg lg:translate-y-[-50%] lg:rounded-lg lg:px-8 lg:py-10',
          props.class
        )
      "
      v-bind="{ ...props, ...emitsAsProps }"
    >
      <div class="flex justify-end" v-if="props.closable">
        <DialogClose
          :class="[
            'rounded-md transition-colors',
            props.dialogCloseClass ? props.dialogCloseClass : '',
          ]"
        >
          <X
            class="bg-transparent stroke-1 hover:stroke-2 focus-visible:stroke-2"
          />
          <span class="sr-only">{{ $t("accessibility.menu.close") }}</span>
        </DialogClose>
      </div>
      <slot />
    </DialogContent>
  </DialogPortal>
</template>
